import { useEffect, useState } from "react";
import { Check, Facebook, Linkedin, Link as LinkIcon, MessageCircle, Share2 } from "lucide-react";

type SocialShareBarProps = {
  title: string;
  url?: string;
  excerpt?: string;
  variant?: "horizontal" | "vertical" | "sticky-rail" | "compact";
  className?: string;
};

// Twitter / X Icon
function TwitterIcon({ size = 16, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function SocialShareBar({
  title,
  url,
  excerpt = "",
  variant = "horizontal",
  className = ""
}: SocialShareBarProps) {
  const [copied, setCopied] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(url || "");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(url || window.location.href);
    }
  }, [url]);

  const shareText = encodeURIComponent(`${title} — ZimSmart Magazine`);
  const encodedUrl = encodeURIComponent(currentUrl);

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://api.whatsapp.com/send?text=${shareText}%20${encodedUrl}`
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: excerpt || title,
          url: currentUrl
        });
      } catch {
        // User canceled or unsupported
      }
    } else {
      handleCopyLink();
    }
  };

  if (variant === "sticky-rail") {
    return (
      <aside className={`mag-sticky-share-rail ${className}`} aria-label="Article sharing tools">
        <div className="mag-share-rail-inner">
          <span className="mag-rail-label">SHARE</span>

          {/* Twitter / X */}
          <a
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="mag-rail-btn twitter"
            title="Share on X (Twitter)"
            aria-label="Share on X (Twitter)"
          >
            <TwitterIcon size={16} />
          </a>

          {/* WhatsApp */}
          <a
            href={shareLinks.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="mag-rail-btn whatsapp"
            title="Share on WhatsApp"
            aria-label="Share on WhatsApp"
          >
            <MessageCircle size={18} />
          </a>

          {/* LinkedIn */}
          <a
            href={shareLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="mag-rail-btn linkedin"
            title="Share on LinkedIn"
            aria-label="Share on LinkedIn"
          >
            <Linkedin size={18} />
          </a>

          {/* Facebook */}
          <a
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="mag-rail-btn facebook"
            title="Share on Facebook"
            aria-label="Share on Facebook"
          >
            <Facebook size={18} />
          </a>

          {/* Copy Link */}
          <button
            type="button"
            onClick={handleCopyLink}
            className={`mag-rail-btn copy ${copied ? "copied" : ""}`}
            title="Copy story link to clipboard"
            aria-label="Copy story link"
          >
            {copied ? <Check size={18} className="text-emerald-600" /> : <LinkIcon size={18} />}
          </button>
        </div>
      </aside>
    );
  }

  return (
    <div className={`mag-social-share-bar ${variant} ${className}`} aria-label="Share this story">
      <span className="mag-share-prompt">Share story:</span>

      <div className="mag-share-buttons-group">
        {/* Twitter / X */}
        <a
          href={shareLinks.twitter}
          target="_blank"
          rel="noopener noreferrer"
          className="mag-share-btn twitter"
          title="Share on X / Twitter"
          aria-label="Share on X / Twitter"
        >
          <TwitterIcon size={15} />
          <span className="mag-btn-text">X / Twitter</span>
        </a>

        {/* WhatsApp */}
        <a
          href={shareLinks.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="mag-share-btn whatsapp"
          title="Share on WhatsApp"
          aria-label="Share on WhatsApp"
        >
          <MessageCircle size={16} />
          <span className="mag-btn-text">WhatsApp</span>
        </a>

        {/* LinkedIn */}
        <a
          href={shareLinks.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="mag-share-btn linkedin"
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
        >
          <Linkedin size={16} />
          <span className="mag-btn-text">LinkedIn</span>
        </a>

        {/* Facebook */}
        <a
          href={shareLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="mag-share-btn facebook"
          title="Share on Facebook"
          aria-label="Share on Facebook"
        >
          <Facebook size={16} />
          <span className="mag-btn-text">Facebook</span>
        </a>

        {/* Copy Link */}
        <button
          type="button"
          onClick={handleCopyLink}
          className={`mag-share-btn copy ${copied ? "copied" : ""}`}
          title="Copy Link to Clipboard"
          aria-label="Copy story link"
        >
          {copied ? (
            <>
              <Check size={15} className="text-emerald-600" />
              <span className="mag-btn-text font-bold text-emerald-700">Copied!</span>
            </>
          ) : (
            <>
              <LinkIcon size={15} />
              <span className="mag-btn-text">Copy Link</span>
            </>
          )}
        </button>

        {/* Mobile native share trigger */}
        {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="mag-share-btn native-share mobile-only"
            title="More Share Options"
            aria-label="More Share Options"
          >
            <Share2 size={15} />
            <span className="mag-btn-text">Share</span>
          </button>
        )}
      </div>
    </div>
  );
}
